/**
 * TanStack-Router-Tree — code-based.
 *
 * Hierarchie:
 *   __root            → Outlet
 *   /login            → AuthLayout > LoginPage     (public)
 *   _admin            → AdminShell > Outlet         (protected layout)
 *     /               → DashboardPage
 *     /properties     → PropertiesPage
 *     /properties/new → PropertyCreatePage
 *     /properties/$id → ApartmentDetailLayout (Workspace mit Sub-Tabs)
 *     /properties/$id/edit → PropertyEditPage (Legacy)
 *     /bookings       → BookingsPage
 *     /bookings/quarantine → BookingQuarantinePage (Admin + Staff)
 *     /bookings/$id   → BookingDetailPage
 *     /users          → UsersPage                  (Admin only)
 *     /system/webhook-events → WebhookEventsPage    (Admin only, Forensik)
 *
 * RouterGuards: beforeLoad prüft Auth + Roles. Unauthorized → redirect to /login.
 */
import { lazy, Suspense } from 'react';
import type { AuthUser } from '@stayon/auth-core';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import { AdminShell } from './layouts/AdminShell';
import { AuthLayout } from './layouts/AuthLayout';
import { IdentityBoundary } from './layouts/IdentityBoundary';
import { LoginPage } from '@/features/auth/LoginPage';
import { parseLoginSearch } from '@/features/auth/loginPrefill';
import { AccountSecurityRoute } from '@/features/account-security/AccountSecurityRoute';
import { ActivateAccountPage } from '@/features/auth/ActivateAccountPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { BookingDetailPage } from '@/features/bookings/BookingDetailPage';
import { BookingQuarantinePage } from '@/features/booking-quarantine';
import { bookingDetailTabFromSearch } from '@/features/bookings/detail-view/bookingDetailTabModel';
import { BookingsPage } from '@/features/bookings/BookingsPage';
import { PAGE_SIZES, type BookingsSearch } from '@/features/bookings/components/bookingsListSearch';
import { CalendarPage } from '@/features/calendar/CalendarPage';
import { validateCalendarSearch } from '@/features/calendar/calendarSearch';
import {
  AVAILABILITY_ROUTE_PRIVILEGE,
  CALENDAR_ROUTE_ROLES,
  PRICING_ROUTE_ROLES,
} from './calendarRouteAccess';
import { AvailabilityDistributionPage } from '@/features/availability-distribution/AvailabilityDistributionPage';
import { validateAvailabilitySearch } from '@/features/availability-distribution/availabilitySearch';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import {
  DynamicPricingConfigurationPage,
  DynamicPricingHistoryPage,
  DynamicPricingOverviewPage,
} from '@/features/dynamic-pricing/DynamicPricingPages';
import {
  legacyCalendarTarget,
  validateLegacyCalendarSearch,
} from '@/features/availability-distribution/legacyCalendarLink';
import { parseDynamicPricingHistorySearch } from '@/features/dynamic-pricing/history/historyUrlState';
import { ChecklistTemplatePage } from '@/features/cleaning/ChecklistTemplatePage';
import { CleaningPage } from '@/features/cleaning/CleaningPage';
import { CleaningDetailPage } from '@/features/cleaning/CleaningDetailPage';
import { CleaningCompletionReportPage } from '@/features/cleaning/completion-report/CleaningCompletionReportPage';
import { InboxPage } from '@/features/inbox/InboxPage';
import { ThreadDetailPage } from '@/features/inbox/ThreadDetailPage';
import { AuditLogPage } from '@/features/system/AuditLogPage';
import { OnboardingLinksPage } from '@/features/system/OnboardingLinksPage';
import { OutboxPage } from '@/features/system/OutboxPage';
import { WebhookEventsPage } from '@/features/system/WebhookEventsPage';
import { IntegrationsPage } from '@/features/settings/IntegrationsPage';
import { TaxSettingsPage } from '@/features/settings/TaxSettingsPage';
import { WorkspaceSettingsPage } from '@/features/settings/workspace/WorkspaceSettingsPage';
import { StripeSettingsPage } from '@/features/settings/StripeSettingsPage';
import { InvoiceTemplatesPage } from '@/features/settings/InvoiceTemplatesPage';
import { SettingsLayout } from '@/features/settings/SettingsLayout';
import { SettingsHubPage } from '@/features/settings/SettingsHubPage';
import { AdvancedSearchPage } from '@/features/search/advanced';
import { PropertiesPage } from '@/features/properties/PropertiesPage';
import { PropertyGroupsPage } from '@/features/property-groups/PropertyGroupsPage';
import {
  ObjectArrivalTab,
  ObjectDetailLayout,
  ObjectGuestPortalTab,
  ObjectHouseRulesTab,
  ObjectMessagesTab,
  ObjectOverviewTab,
  ObjectParkingTab,
  ObjectResourcesTab,
  ObjectPublicationTab,
  ObjectStammdatenTab,
} from '@/features/property-groups/object-workspace';
import { ParkingPage } from '@/features/property-groups/ParkingPage';
import { AkteRegisterPage } from '@/features/properties/akte/AkteRegisterPage';
import { GlobalPropertyAktePage } from '@/features/properties/akte/GlobalPropertyAktePage';
import { WorkspaceAkteNavigator } from '@/features/properties/akte/WorkspaceAkteNavigator';
import { PropertyInsightsPage } from '@/features/properties/finance-insights/PropertyInsightsPage';
import { PropertyCreatePage } from '@/features/properties/PropertyCreatePage';
import { PropertyEditPage } from '@/features/properties/PropertyEditPage';
import {
  ApartmentDetailLayout,
  AuditTab,
  BilderTab,
  ChannelsTab,
  ParkingTab,
  OverviewTab,
  PreiseTab,
  ReinigungTab,
  StammdatenTab,
} from '@/features/properties/apartment-workspace';
import { UsersPage } from '@/features/users/UsersPage';
import { UserDetailPage } from '@/features/users/detail/UserDetailPage';
import { PluginsPage } from '@/features/plugins/PluginsPage';
import { PluginDetailPage } from '@/features/plugins/PluginDetailPage';
import { PluginConnectPage } from '@/features/plugins/PluginConnectPage';
import { parsePluginSearch, parsePluginLinkSearch } from '@/features/plugins/pluginPresentation';
import {
  usersFiltersFromSearch,
  usersPageFromSearch,
  usersSearchFromState,
  type UsersSearch,
} from '@/features/users/usersListSearch';
import { CompaniesPage } from '@/features/companies/CompaniesPage';
import { CleaningTeamsPage } from '@/features/cleaning-teams/CleaningTeamsPage';
import { WorkspacesPage } from '@/features/workspaces';
import { SmartLockDevicesPage } from '@/features/smart-lock/SmartLockDevicesPage';
import { SmartLockMappingPage } from '@/features/smart-lock/SmartLockMappingPage';
import {
  createAuthStore,
  useAuth,
  can,
  dashboardLanding,
  hasAnyRole,
  isPlatformAdmin,
  isPlatformWorkspace,
  isWorkspaceAdmin,
  ROLE_GROUPS,
  ROLES,
  type Role,
} from '@/shared/auth';
import { PRIVILEGES, type PrivilegeId } from '@stayon/auth-core';
import { CLEANING_QUEUE_SURFACE, DASHBOARD_SURFACE } from '@/shared/auth/privilegeSurfaces';

const rootRoute = createRootRoute({
  component: () => <Outlet />,
});

function hasSessionContinuity(state: ReturnType<ReturnType<typeof createAuthStore>['getState']>) {
  return (
    state.user !== null &&
    (state.status === 'authenticated' ||
      state.status === 'refreshing' ||
      state.status === 'reconnecting')
  );
}

// ─── Auth-Branch (public) ───────────────────────────────────────────────────
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: parseLoginSearch,
  beforeLoad: () => {
    const store = createAuthStore();
    if (hasSessionContinuity(store.getState())) {
      throw redirect({ to: '/' });
    }
  },
  component: () => (
    <AuthLayout>
      <LoginPage />
    </AuthLayout>
  ),
});

// ─── Guest-Portal (public) ──────────────────────────────────────────────────
// Lazy: der Guest-Chunk inkl. seiner drei Brand-Fonts bleibt aus dem Admin-Initial-Bundle.
// Invite-Aktivierung (public): Landing des Account-aktivieren-Links aus der Welcome-Mail.
const activateAccountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/activate-account',
  component: () => (
    <AuthLayout greeting="new">
      <ActivateAccountPage />
    </AuthLayout>
  ),
});

// Passwort-Zuruecksetzen (public): ohne diese beiden Seiten haette ein ausgesperrter
// WorkspaceAdmin keinen Weg zurueck — Support-Zugriff ist bewusst nicht gebaut.
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: () => (
    <AuthLayout>
      <ForgotPasswordPage />
    </AuthLayout>
  ),
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: () => (
    <AuthLayout>
      <ResetPasswordPage />
    </AuthLayout>
  ),
});

const GuestOnboardingPage = lazy(() =>
  import('@/features/guest/GuestOnboardingPage').then((m) => ({
    default: m.GuestOnboardingPage,
  })),
);

const FinanceJournalPage = lazy(() =>
  import('@/features/finance/journal/FinanceJournalPage').then((module) => ({
    default: module.FinanceJournalPage,
  })),
);
const InvoiceBuilderPage = lazy(() =>
  import('@/features/finance/builder/InvoiceBuilderPage').then((module) => ({
    default: module.InvoiceBuilderPage,
  })),
);
const InvoiceRecipientReplacementPage = lazy(() =>
  import('@/features/finance/recipient/InvoiceRecipientReplacementPage').then((module) => ({
    default: module.InvoiceRecipientReplacementPage,
  })),
);
const InvoicesPage = lazy(() =>
  import('@/features/finance/invoices/InvoicesPage').then((module) => ({
    default: module.InvoicesPage,
  })),
);
const FinancialAutomationPage = lazy(() =>
  import('@/features/finance/automation/FinancialAutomationPage').then((module) => ({
    default: module.FinancialAutomationPage,
  })),
);
const GuestReAuthPage = lazy(() =>
  import('@/features/guest/GuestReAuthPage').then((m) => ({
    default: m.GuestReAuthPage,
  })),
);
const GuestPaymentPage = lazy(() =>
  import('@/features/guest/GuestPaymentPage').then((m) => ({
    default: m.GuestPaymentPage,
  })),
);
const GuestUpgradePaymentPage = lazy(() =>
  import('@/features/guest/GuestUpgradePaymentPage').then((m) => ({
    default: m.GuestUpgradePaymentPage,
  })),
);
const GuestCheckInPage = lazy(() =>
  import('@/features/guest/GuestCheckInPage').then((m) => ({
    default: m.GuestCheckInPage,
  })),
);
const TeamLegalPage = lazy(() =>
  import('@/features/auth/TeamLegalPage').then((m) => ({ default: m.TeamLegalPage })),
);
const GuestLegalPage = lazy(() =>
  import('@/features/guest/legal/GuestLegalPage').then((m) => ({
    default: m.GuestLegalPage,
  })),
);

const guestOnboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/onboarding',
  component: () => (
    <Suspense fallback={null}>
      <GuestOnboardingPage />
    </Suspense>
  ),
});

const guestReAuthRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/re-auth',
  component: () => (
    <Suspense fallback={null}>
      <GuestReAuthPage />
    </Suspense>
  ),
});

const guestPaymentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/pay',
  component: () => (
    <Suspense fallback={null}>
      <GuestPaymentPage />
    </Suspense>
  ),
});

const guestUpgradePaymentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/upgrade-pay',
  component: () => (
    <Suspense fallback={null}>
      <GuestUpgradePaymentPage />
    </Suspense>
  ),
});

const guestCheckInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/checkin',
  component: () => (
    <Suspense fallback={null}>
      <GuestCheckInPage />
    </Suspense>
  ),
});

// Die Rechtstexte sind oeffentlich: wer eine Einwilligung erteilen soll, muss vorher lesen
// koennen, wozu — und der Aufruf aus dem Formular heraus darf keine Sitzung voraussetzen.
const guestPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/legal/datenschutz',
  component: () => (
    <Suspense fallback={null}>
      <GuestLegalPage document="datenschutz" />
    </Suspense>
  ),
});

// Das Impressum ist von JEDER Seite aus erreichbar (Fusszeile der Portal-Huelle): § 5 DDG
// verlangt „leicht erkennbar, unmittelbar erreichbar und staendig verfuegbar" — ein Link, den es
// nur auf der Startseite gibt, erfuellt das letzte Wort nicht.
const guestImprintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/legal/impressum',
  component: () => (
    <Suspense fallback={null}>
      <GuestLegalPage document="impressum" />
    </Suspense>
  ),
});

// Datenschutz und Impressum des Teams sind öffentlich: lesen können muss sie, wer sich erst
// anmelden will.
const teamPrivacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/datenschutz',
  component: () => (
    <Suspense fallback={null}>
      <TeamLegalPage document="datenschutz" />
    </Suspense>
  ),
});
const teamImprintRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/legal/impressum',
  component: () => (
    <Suspense fallback={null}>
      <TeamLegalPage document="impressum" />
    </Suspense>
  ),
});

const guestTermsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guest/legal/beherbergung',
  component: () => (
    <Suspense fallback={null}>
      <GuestLegalPage document="beherbergung" />
    </Suspense>
  ),
});

// ─── Admin-Branch (protected) ───────────────────────────────────────────────
function requireAuth(requiredRoles?: Role[]) {
  const store = createAuthStore();
  const state = store.getState();
  if (!hasSessionContinuity(state)) {
    throw redirect({ to: '/login' });
  }
  // Selbe Match-Logik wie die Nav-Sichtbarkeit (canAccess) → Guard und Sidebar driften nicht auseinander.
  if (requiredRoles && !hasAnyRole(state.user, ...requiredRoles)) {
    throw redirect({ to: dashboardLanding(state.user) ?? '/' });
  }
}

function requirePrivilege(privilege: PrivilegeId) {
  const store = createAuthStore();
  const state = store.getState();
  if (!hasSessionContinuity(state)) {
    throw redirect({ to: '/login' });
  }
  if (!can(state.user, privilege)) {
    throw redirect({ to: dashboardLanding(state.user) ?? '/account/security' });
  }
}

function deniedLanding(user: AuthUser | null) {
  return dashboardLanding(user) ?? '/';
}

/**
 * Einstieg in den Einstellungen-Hub: beide Admin-Achsen. Wer eine der beiden Rollen trägt, darf
 * das Layout betreten — welche Einträge er sieht, entscheiden Kind-Guards und Nav-Filter.
 */
function requireSettingsHub() {
  const store = createAuthStore();
  const state = store.getState();
  if (!hasSessionContinuity(state)) {
    throw redirect({ to: '/login' });
  }
  if (
    !isWorkspaceAdmin(state.user) &&
    !isPlatformAdmin(state.user) &&
    !can(state.user, PRIVILEGES.cleaningTeamsManage)
  ) {
    throw redirect({ to: deniedLanding(state.user) });
  }
}

/**
 * Guard für Plattformflächen. Spiegelt die Server-Policy `RequirePlatformAdmin`: die Rolle
 * allein genügt nicht, der Workspace muss auch vom Typ Platform sein. Eine reine Rollenprüfung
 * liesse einen PlatformAdmin in einem Customer-Workspace auf Seiten, die der Server mit 403
 * beantwortet.
 */
function requirePlatform() {
  const store = createAuthStore();
  const state = store.getState();
  if (!hasSessionContinuity(state)) {
    throw redirect({ to: '/login' });
  }
  if (!isPlatformAdmin(state.user)) {
    throw redirect({ to: deniedLanding(state.user) });
  }
}

/**
 * Guard für die **kontoweiten** Channex-Flächen. Spiegelt `RequirePlatformWorkspace` +
 * Rollenliste am Controller: ihr Gegenstand ist das Channex-Konto, nicht das Inventar eines
 * Workspaces. Eine Support-Sitzung bekäme dort nur den Ausschnitt ihres Ziel-Workspace und kein
 * Fenster ohne Objekt; ihr Mapping läuft über den Kanäle-Reiter des Objekts.
 */
function requirePlatformWorkspace(requiredRoles: Role[]) {
  const store = createAuthStore();
  const state = store.getState();
  if (!hasSessionContinuity(state)) {
    throw redirect({ to: '/login' });
  }
  if (!isPlatformWorkspace(state.user) || !hasAnyRole(state.user, ...requiredRoles)) {
    throw redirect({ to: deniedLanding(state.user) });
  }
}

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: '_admin',
  beforeLoad: () => requireAuth(),
  component: () => (
    <IdentityBoundary>
      <AdminShell>
        <Outlet />
      </AdminShell>
    </IdentityBoundary>
  ),
});

const pluginsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/plugins',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceMembers),
  component: PluginsPage,
});
const pluginDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/plugins/$pluginId',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceMembers),
  validateSearch: parsePluginSearch,
  component: PluginDetailPage,
});
const pluginConnectRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/plugins/connect',
  validateSearch: parsePluginLinkSearch,
  component: function PluginConnectionEntry() {
    const { user, status } = useAuth();
    if (!user || status === 'unauthenticated')
      return (
        <AuthLayout>
          <LoginPage embedded />
        </AuthLayout>
      );
    return (
      <IdentityBoundary>
        <PluginConnectPage />
      </IdentityBoundary>
    );
  },
});

const dashboardRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  // Scoped Rollen (Owner/Kunde, Reinigung) bekommen statt des Admin/Staff-only-Dashboards
  // ein passendes, rollenfokussiertes Landing.
  beforeLoad: () => {
    requirePrivilege(DASHBOARD_SURFACE.privilege);
    const state = createAuthStore().getState();
    const landing = dashboardLanding(state.user);
    if (landing) throw redirect({ to: landing });
  },
  component: DashboardPage,
});

const accountSecurityRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/account/security',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceMembers),
  component: AccountSecurityRoute,
});

const propertiesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/properties',
  beforeLoad: () => requireAuth(ROLE_GROUPS.operationsOwner),
  component: PropertiesPage,
});

const akteRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/akte',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: WorkspaceAkteNavigator,
});

const globalPropertyAkteRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/akte/$propertyId',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: GlobalPropertyAktePage,
});

// Rollen wie am InvoicesController (WorkspaceAdmin + Staff) — Housekeeping und Owner sehen
// Rechnungen nicht.
const invoicesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/invoices',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: () => (
    <Suspense fallback={null}>
      <InvoicesPage />
    </Suspense>
  ),
});

const financeRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/finance',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staffOwner),
  component: () => (
    <Suspense fallback={null}>
      <FinanceJournalPage />
    </Suspense>
  ),
});

const financialAutomationRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/finance/automation',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  validateSearch: (search: Record<string, unknown>): { reiter?: 'verarbeitung' } =>
    search.reiter === 'verarbeitung' ? { reiter: 'verarbeitung' } : {},
  component: () => (
    <Suspense fallback={null}>
      <FinancialAutomationPage />
    </Suspense>
  ),
});

const dynamicPricingOverviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/dynamic-pricing',
  beforeLoad: () => requireAuth(PRICING_ROUTE_ROLES),
  component: DynamicPricingOverviewPage,
});

// Der Preisautomatik-Kalender ging in Verfügbarkeit & Vertrieb auf; alte Links landen im Monat
// des Apartments am gewählten Tag oder im Grundkalender ab demselben Tag.
const dynamicPricingCalendarRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/dynamic-pricing/calendar',
  validateSearch: validateLegacyCalendarSearch,
  beforeLoad: ({ search }) => {
    requireAuth(PRICING_ROUTE_ROLES);
    throw redirect({ to: '/availability-distribution', search: legacyCalendarTarget(search) });
  },
});

const dynamicPricingConfigurationRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/dynamic-pricing/configuration',
  beforeLoad: () => requireAuth(PRICING_ROUTE_ROLES),
  validateSearch: (search: Record<string, unknown>): { apartment?: string } => ({
    apartment: typeof search.apartment === 'string' ? search.apartment : undefined,
  }),
  component: DynamicPricingConfigurationPage,
});

const dynamicPricingHistoryRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/dynamic-pricing/history',
  beforeLoad: () => requireAuth(PRICING_ROUTE_ROLES),
  validateSearch: parseDynamicPricingHistorySearch,
  component: DynamicPricingHistoryPage,
});

const advancedSearchRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/search/advanced',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staffOwner),
  component: AdvancedSearchPage,
});

const propertyGroupsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/property-groups',
  beforeLoad: () => requirePrivilege(PRIVILEGES.propertyGroupsView),
  component: PropertyGroupsPage,
});

const propertyGroupDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/property-groups/$id',
  beforeLoad: () => requirePrivilege(PRIVILEGES.propertyGroupsView),
  component: ObjectDetailLayout,
});

const objectOverviewRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/',
  component: ObjectOverviewTab,
});

const objectStammdatenRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/stammdaten',
  component: ObjectStammdatenTab,
});

const objectArrivalRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/anfahrt',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ObjectArrivalTab,
});

const objectParkingRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/parken',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ObjectParkingTab,
});

const objectResourcesRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/ressourcen',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ObjectResourcesTab,
});

const objectHouseRulesRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/hausordnung',
  component: ObjectHouseRulesTab,
});

const objectPublicationRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/veroeffentlichung',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ObjectPublicationTab,
});

const objectMessagesRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/nachrichten',
  beforeLoad: () => requirePrivilege(PRIVILEGES.guestExperienceView),
  component: ObjectMessagesTab,
});

const objectGuestPortalRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/gastportal',
  beforeLoad: () => requirePrivilege(PRIVILEGES.guestExperienceView),
  component: ObjectGuestPortalTab,
});

// Frühere Adressen des Objekt-Arbeitsraums. Gespeicherte Links und Lesezeichen landen am neuen Ort,
// statt auf einer leeren Unterseite; die Rechte prüft danach die Zielroute.
const objectLegacyParkingRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/parkplaetze',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/property-groups/$id/parken', params, replace: true });
  },
});

const objectLegacyCommunicationRoute = createRoute({
  getParentRoute: () => propertyGroupDetailRoute,
  path: '/kommunikation',
  beforeLoad: ({ params }) => {
    throw redirect({ to: '/property-groups/$id/nachrichten', params, replace: true });
  },
});

const parkingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/parking',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ParkingPage,
});

const propertyCreateRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/properties/new',
  validateSearch: (search: Record<string, unknown>): { objectId?: string } => ({
    objectId: typeof search.objectId === 'string' ? search.objectId : undefined,
  }),
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: PropertyCreatePage,
});

// ─── Apartment-Workspace mit Sub-Tabs ─────────────────────────────────────
const propertyDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/properties/$id',
  beforeLoad: () => requireAuth(),
  component: ApartmentDetailLayout,
});

const propertyOverviewRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/',
  beforeLoad: () => requireAuth(ROLE_GROUPS.operationsOwner),
  component: OverviewTab,
});

const propertyStammdatenRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/stammdaten',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: StammdatenTab,
});

const propertyBilderRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/bilder',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: BilderTab,
});

const propertyParkingRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/parking',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ParkingTab,
});

const propertyChannelsRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/channels',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ChannelsTab,
});

const propertyPreiseRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/preise',
  beforeLoad: () => requirePrivilege(AVAILABILITY_ROUTE_PRIVILEGE),
  component: PreiseTab,
});

const propertyAuditRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/audit',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: AuditTab,
});

// Legacy-Sub-Pages kommen als Tabs unter dem Layout — sie behalten ihre URL.
const propertyEditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/properties/$id/edit',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: PropertyEditPage,
});

const propertyAkteRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/akte',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: AkteRegisterPage,
});

const propertyFinanceRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/finance',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staffOwner),
  component: PropertyInsightsPage,
});

const propertyReinigungRoute = createRoute({
  getParentRoute: () => propertyDetailRoute,
  path: '/reinigung',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ReinigungTab,
});

// Einstellungen-Hub als pathless Layout-Route: bündelt Verwaltung, Abrechnung,
// Integrationen und System hinter einer internen Sub-Navigation. Pathless (id-basiert),
// damit die Kind-Routen ihre absoluten Pfade behalten und bestehende URLs stabil bleiben.
const settingsLayoutRoute = createRoute({
  getParentRoute: () => adminRoute,
  id: '_settings',
  // Beide Achsen: unter diesem Layout haengen Workspace- UND Plattformflaechen. Ein reiner
  // workspaceAdmin-Guard sperrte einen PlatformAdmin ohne Workspace-Rolle aus, BEVOR das
  // requirePlatform() der Kind-Route ueberhaupt laeuft (beforeLoad laeuft von der Wurzel
  // abwaerts). Die Kind-Routen und isSettingsItemVisible filtern anschliessend selbst.
  beforeLoad: () => requireSettingsHub(),
  component: SettingsLayout,
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings',
  // Eigenständiger Guard (nicht nur der Layout-Guard): der Hub-Index listet alle
  // Admin-Bereiche, darum fail-closed konsistent zu den Geschwister-Routen.
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: SettingsHubPage,
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/workspace',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: WorkspaceSettingsPage,
});

const taxSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/tax',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: TaxSettingsPage,
});

const stripeSettingsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/stripe',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: StripeSettingsPage,
});

const invoiceTemplatesRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/invoice-templates',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: InvoiceTemplatesPage,
});

const bookingsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/bookings',
  beforeLoad: () => requireAuth(ROLE_GROUPS.operationsOwner),
  // Filter, Sortierung und Seite gehören in die URL: Reload, Zurück-Taste und ein geteilter Link
  // sollen denselben Arbeitskontext wiederherstellen (FRONTEND_GUIDELINES §5).
  //
  // Der explizite Rückgabetyp ist nicht kosmetisch: Ohne ihn leitet TanStack Router aus dem
  // Objektliteral Pflichtschlüssel ab und verlangt `search` an jedem `Link to="/bookings"`.
  validateSearch: (search: Record<string, unknown>): BookingsSearch => ({
    status: typeof search.status === 'string' ? search.status : undefined,
    dateFrom: typeof search.dateFrom === 'string' ? search.dateFrom : undefined,
    dateTo: typeof search.dateTo === 'string' ? search.dateTo : undefined,
    propertyId: typeof search.propertyId === 'string' ? search.propertyId : undefined,
    q: typeof search.q === 'string' ? search.q : undefined,
    sort: typeof search.sort === 'string' ? search.sort : undefined,
    page:
      Number.isFinite(Number(search.page)) && Number(search.page) > 1
        ? Math.floor(Number(search.page))
        : undefined,
    size: PAGE_SIZES.some((size) => size === Number(search.size)) ? Number(search.size) : undefined,
    past: search.past === true || search.past === 'true' ? true : undefined,
  }),
  component: BookingsPage,
});

const bookingDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/bookings/$id',
  beforeLoad: () => requireAuth(ROLE_GROUPS.operationsOwner),
  validateSearch: (search: Record<string, unknown>): { tab?: string } => {
    const tab = bookingDetailTabFromSearch(search.tab);
    return { tab: tab === 'overview' ? undefined : tab };
  },
  component: BookingDetailPage,
});

// Kanalbuchungen ohne Buchung und „Erneut verarbeiten" — Rollen wie im Backend
// (WorkspaceAdminAndStaff), in jedem Workspace. Statisch vor `/bookings/$id`.
const bookingQuarantineRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/bookings/quarantine',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: BookingQuarantinePage,
});

// Eine eigene Seite und kein Dialog: die PDF-Vorschau braucht Platz, und wer eine Rechnung
// schreibt, macht das nicht nebenbei. Rollen wie im Backend — Admin und Staff, kein Owner.
const invoiceBuilderRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/bookings/$id/rechnung/$draftId',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: InvoiceBuilderPage,
});

const invoiceRecipientReplacementRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/bookings/$id/rechnung/$invoiceId/empfaenger',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: () => (
    <Suspense fallback={null}>
      <InvoiceRecipientReplacementPage />
    </Suspense>
  ),
});

const usersRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/users',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  // Suche, Rolle, Zustand und Seite gehören in die URL (§5) — die Bereinigung passiert in
  // usersFiltersFromSearch, damit sie testbar bleibt und nicht doppelt in der Route steht.
  validateSearch: (search: Record<string, unknown>): UsersSearch =>
    usersSearchFromState({
      filters: usersFiltersFromSearch(search as UsersSearch),
      page: usersPageFromSearch(search as UsersSearch),
    }),
  component: UsersPage,
});

// Bewusst nicht im Einstellungs-Layout: dessen 240-px-Rail lässt der Fläche auch auf 1440 px
// nur rund 714 px, und die Akte ist eine zweispaltige Arbeitsfläche wie der Buchungsarbeitsraum.
// Der Rückweg steht als Link im Kopf, nicht über die Bereichsliste.
const userDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/users/$id',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: UserDetailPage,
});

const companiesRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/companies',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: CompaniesPage,
});

const cleaningTeamsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/cleaning-teams',
  beforeLoad: () => requirePrivilege(PRIVILEGES.cleaningTeamsManage),
  component: CleaningTeamsPage,
});

const integrationsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/settings/integrations',
  beforeLoad: () => requirePlatformWorkspace(ROLE_GROUPS.workspaceAdmin),
  component: IntegrationsPage,
});

const calendarRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/calendar',
  beforeLoad: () => requireAuth(CALENDAR_ROUTE_ROLES),
  validateSearch: validateCalendarSearch,
  component: CalendarPage,
});

const availabilityDistributionRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/availability-distribution',
  beforeLoad: () => requirePrivilege(AVAILABILITY_ROUTE_PRIVILEGE),
  validateSearch: validateAvailabilitySearch,
  component: AvailabilityDistributionPage,
});

const cleaningRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/cleaning',
  beforeLoad: () => requirePrivilege(CLEANING_QUEUE_SURFACE.privilege),
  validateSearch: (search: Record<string, unknown>) => ({
    view: search.view === 'calendar' ? ('calendar' as const) : undefined,
    date: typeof search.date === 'string' ? search.date : undefined,
    status: ['free', 'active', 'overdue', 'completed'].includes(String(search.status))
      ? (search.status as 'free' | 'active' | 'overdue' | 'completed')
      : undefined,
    assignee: typeof search.assignee === 'string' ? search.assignee : undefined,
  }),
  component: CleaningPage,
});

const cleaningCompletionReportRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/cleaning/reports',
  beforeLoad: () => requirePrivilege(PRIVILEGES.cleaningReportsView),
  validateSearch: (search: Record<string, unknown>) => ({
    from: typeof search.from === 'string' ? search.from : undefined,
    to: typeof search.to === 'string' ? search.to : undefined,
    period: search.period === 'range' ? ('range' as const) : undefined,
    objectId: typeof search.objectId === 'string' ? search.objectId : undefined,
    propertyId: typeof search.propertyId === 'string' ? search.propertyId : undefined,
    assignedToUserId:
      typeof search.assignedToUserId === 'string' ? search.assignedToUserId : undefined,
    selectedPropertyId:
      typeof search.selectedPropertyId === 'string' ? search.selectedPropertyId : undefined,
  }),
  component: CleaningCompletionReportPage,
});

const inboxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/inbox',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: InboxPage,
});

const inboxThreadRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/inbox/$id',
  beforeLoad: () => requireAuth(ROLE_GROUPS.staff),
  component: ThreadDetailPage,
});

const cleaningDetailRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/cleaning/$id',
  beforeLoad: () => requirePrivilege(CLEANING_QUEUE_SURFACE.privilege),
  component: CleaningDetailPage,
});

const checklistTemplateRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/properties/$propertyId/checklist',
  beforeLoad: () => requirePrivilege(PRIVILEGES.cleaningAssignmentsManage),
  component: ChecklistTemplatePage,
});

const smartLockDevicesRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/smart-lock/devices',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: SmartLockDevicesPage,
});

const smartLockMappingRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/smart-lock/mapping',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  component: SmartLockMappingPage,
});

const workspacesRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/platform/workspaces',
  beforeLoad: () => requirePlatform(),
  component: WorkspacesPage,
});

const systemAuditRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/system/audit',
  beforeLoad: () => requirePlatform(),
  component: AuditLogPage,
});

const systemOutboxRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/system/outbox',
  beforeLoad: () => requirePlatform(),
  component: OutboxPage,
});

// Bleibt unter Kommunikation (nicht im Einstellungen-Hub): Gäste & QR-Poster.
const systemOnboardingLinksRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/system/onboarding-links',
  beforeLoad: () => requireAuth(ROLE_GROUPS.workspaceAdmin),
  validateSearch: (search: Record<string, unknown>): { tab?: 'qr-poster' } => ({
    tab: search.tab === 'qr-poster' ? 'qr-poster' : undefined,
  }),
  component: OnboardingLinksPage,
});

const systemWebhookEventsRoute = createRoute({
  getParentRoute: () => settingsLayoutRoute,
  path: '/system/webhook-events',
  beforeLoad: () => requirePlatform(),
  component: WebhookEventsPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  pluginConnectRoute,
  activateAccountRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  guestOnboardingRoute,
  guestReAuthRoute,
  guestPaymentRoute,
  guestUpgradePaymentRoute,
  guestCheckInRoute,
  guestPrivacyRoute,
  guestTermsRoute,
  guestImprintRoute,
  teamPrivacyRoute,
  teamImprintRoute,
  adminRoute.addChildren([
    dashboardRoute,
    pluginsRoute,
    pluginDetailRoute,
    accountSecurityRoute,
    invoicesRoute,
    financeRoute,
    financialAutomationRoute,
    dynamicPricingOverviewRoute,
    dynamicPricingCalendarRoute,
    dynamicPricingConfigurationRoute,
    dynamicPricingHistoryRoute,
    propertiesRoute,
    akteRoute,
    globalPropertyAkteRoute,
    advancedSearchRoute,
    propertyGroupsRoute,
    propertyGroupDetailRoute.addChildren([
      objectOverviewRoute,
      objectStammdatenRoute,
      objectArrivalRoute,
      objectParkingRoute,
      objectResourcesRoute,
      objectHouseRulesRoute,
      objectMessagesRoute,
      objectGuestPortalRoute,
      objectPublicationRoute,
      objectLegacyParkingRoute,
      objectLegacyCommunicationRoute,
    ]),
    parkingRoute,
    propertyCreateRoute,
    propertyDetailRoute.addChildren([
      propertyOverviewRoute,
      propertyStammdatenRoute,
      propertyBilderRoute,
      propertyChannelsRoute,
      propertyPreiseRoute,
      propertyReinigungRoute,
      propertyAkteRoute,
      propertyParkingRoute,
      propertyFinanceRoute,
      propertyAuditRoute,
    ]),
    propertyEditRoute,
    userDetailRoute,
    settingsLayoutRoute.addChildren([
      smartLockDevicesRoute,
      smartLockMappingRoute,
      settingsIndexRoute,
      usersRoute,
      companiesRoute,
      cleaningTeamsRoute,
      invoiceTemplatesRoute,
      workspaceSettingsRoute,
      taxSettingsRoute,
      stripeSettingsRoute,
      integrationsRoute,
      workspacesRoute,
      systemAuditRoute,
      systemOutboxRoute,
      systemWebhookEventsRoute,
    ]),
    bookingsRoute,
    bookingQuarantineRoute,
    invoiceBuilderRoute,
    invoiceRecipientReplacementRoute,
    bookingDetailRoute,
    calendarRoute,
    availabilityDistributionRoute,
    cleaningRoute,
    cleaningCompletionReportRoute,
    cleaningDetailRoute,
    inboxRoute,
    inboxThreadRoute,
    checklistTemplateRoute,
    systemOnboardingLinksRoute,
  ]),
]);

// Gescrollt wird im Inhalt des Gerüsts, nicht im Fenster: der Router setzt diesen bei jeder
// Navigation zurück (und lässt ihn bei `resetScroll: false` stehen).
export const router = createRouter({ routeTree, scrollToTopSelectors: ['#main-content'] });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

export { ROLES };
